/**
 * Labels for mock audit log `action` codes and shared severity/scope tags.
 * Keys are derived from API codes by replacing `.` and `-` with `_` (see `auditActionTranslationKey`).
 */

const actionsEn = {
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
  applied: 'Applied',
  scheduled: 'Scheduled',
  completed: 'Completed',
  account_profile_updated: 'Account profile updated',
  account_connected_provider_added: 'Sign-in provider linked',
  account_connected_provider_removed: 'Sign-in provider unlinked',
  account_security_updated: 'Account security settings updated',
  account_session_revoked: 'Session revoked',
  account_session_revoke_all: 'All other sessions revoked',
  account_passkey_added: 'Passkey added',
  account_passkey_removed: 'Passkey removed',
  account_recovery_codes_regenerated: 'Recovery codes regenerated',
  account_trusted_device_revoked: 'Trusted device revoked',
  // Auth events (from Core UserLog)
  account_login_credentials: 'Signed in with password',
  account_login_google: 'Signed in with Google',
  account_login_entraid: 'Signed in with Entra ID',
  account_login_mfa: 'Signed in with MFA',
  account_login_email_otp: 'Signed in with email code',
  account_login_apple: 'Signed in with Apple',
  account_login_passkey: 'Signed in with passkey',
  account_login_firebase: 'Signed in (Firebase)',
  account_logout: 'Signed out',
  account_register: 'Account registered',
  account_register_google: 'Registered with Google',
  account_token_refresh: 'Session refreshed',
  account_token_sent: 'Verification token sent',
  account_token_confirmed: 'Verification token confirmed',
  account_invited: 'Invitation accepted',
  account_password_changed: 'Password changed',
  account_password_mfa_changed: 'Password changed (via MFA)',
  account_password_reset_requested: 'Password reset email sent',
  account_password_reset_confirmed: 'Password reset completed',
  // MFA events (from Core MFAAuditLog)
  account_mfa_otp_sent: 'MFA code sent',
  account_mfa_otp_sent_fail: 'MFA code send failed',
  account_mfa_otp_verified: 'MFA code verified',
  account_mfa_otp_verify_fail: 'MFA code verification failed',
  account_mfa_backup_code_verified: 'Backup code used',
  account_mfa_backup_code_verify_fail: 'Backup code verification failed',
  account_mfa_trusted_device_added: 'Trusted device added',
  account_mfa_trusted_device_removed: 'Trusted device removed',
  account_mfa_trusted_devices_cleared: 'All trusted devices removed',
  account_mfa_account_locked: 'Account temporarily locked',
  account_mfa_enabled: 'MFA enabled',
  account_mfa_disabled: 'MFA disabled',
  account_mfa_backup_codes_regenerated: 'Recovery codes regenerated',
  // Passkey events
  account_passkey_registered: 'Passkey registered',
  account_passkey_deleted: 'Passkey removed',
  account_passkey_renamed: 'Passkey renamed',
  account_passkey_auth_success: 'Passkey authentication succeeded',
  account_passkey_auth_fail: 'Passkey authentication failed',
  workspace_security_auth_method_updated: 'Auth method updated',
  workspace_created: 'Workspace created',
  workspace_updated: 'Workspace updated',
  workspace_security_updated: 'Workspace security updated',
  workspace_security_policy_updated: 'Workspace security policy updated',
  workspace_member_invited: 'Member invited',
  workspace_member_updated: 'Member updated',
  workspace_member_removed: 'Member removed',
  workspace_role_created: 'Workspace role created',
  workspace_role_updated: 'Workspace role updated',
  workspace_role_deleted: 'Workspace role deleted',
  workspace_sso_connection_created: 'SSO connection created',
  workspace_sso_connection_updated: 'SSO connection updated',
  workspace_sso_connection_removed: 'SSO connection removed',
  workspace_sso_certificate_rotated: 'SSO certificate rotated',
  workspace_scim_connection_updated: 'SCIM connection updated',
  workspace_scim_connection_removed: 'SCIM connection removed',
  workspace_scim_sync: 'SCIM sync',
  workspace_domain_rule_created: 'Domain rule created',
  workspace_domain_rule_updated: 'Domain rule updated',
  workspace_domain_rule_deleted: 'Domain rule deleted',
  workspace_api_key_created: 'Workspace API key created',
  workspace_api_key_revoked: 'Workspace API key revoked',
  app_created: 'Application created',
  app_roles_seeded: 'Default app roles provisioned (mock)',
  app_updated: 'Application updated',
  app_permission_created: 'App custom permission created',
  app_permission_deleted: 'App custom permission deleted',
  app_role_created: 'App role created',
  app_role_updated: 'App role updated',
  app_role_deleted: 'App role deleted',
  app_credential_created: 'App credential created',
  app_credential_rotated: 'App credential rotated',
  app_credential_revoked: 'App credential revoked',
  app_webhook_created: 'Webhook created',
  app_webhook_updated: 'Webhook updated',
  app_webhook_deleted: 'Webhook deleted',
  app_webhook_delivery_failed: 'Webhook delivery failed'
} as const

const actionsRo: Record<keyof typeof actionsEn, string> = {
  created: 'Creat',
  updated: 'Actualizat',
  deleted: 'Șters',
  applied: 'Aplicat',
  scheduled: 'Programat',
  completed: 'Finalizat',
  account_profile_updated: 'Profilul contului a fost actualizat',
  account_connected_provider_added: 'Furnizor de autentificare conectat',
  account_connected_provider_removed: 'Furnizor de autentificare deconectat',
  account_security_updated:
    'Setările de securitate ale contului au fost actualizate',
  account_session_revoked: 'Sesiune revocată',
  account_session_revoke_all: 'Toate celelalte sesiuni au fost revocate',
  account_passkey_added: 'Passkey adăugat',
  account_passkey_removed: 'Passkey eliminat',
  account_recovery_codes_regenerated: 'Coduri de recuperare regenerate',
  account_trusted_device_revoked: 'Dispozitiv de încredere revocat',
  account_login_credentials: 'Autentificare cu parolă',
  account_login_google: 'Autentificare cu Google',
  account_login_entraid: 'Autentificare cu Entra ID',
  account_login_mfa: 'Autentificare cu MFA',
  account_login_email_otp: 'Autentificare cu cod e-mail',
  account_login_apple: 'Autentificare cu Apple',
  account_login_passkey: 'Autentificare cu passkey',
  account_login_firebase: 'Autentificare (Firebase)',
  account_logout: 'Deconectare',
  account_register: 'Cont înregistrat',
  account_register_google: 'Înregistrare cu Google',
  account_token_refresh: 'Sesiune reînnoită',
  account_token_sent: 'Token de verificare trimis',
  account_token_confirmed: 'Token de verificare confirmat',
  account_invited: 'Invitație acceptată',
  account_password_changed: 'Parolă schimbată',
  account_password_mfa_changed: 'Parolă schimbată (prin MFA)',
  account_password_reset_requested: 'E-mail de resetare a parolei trimis',
  account_password_reset_confirmed: 'Resetarea parolei finalizată',
  account_mfa_otp_sent: 'Cod MFA trimis',
  account_mfa_otp_sent_fail: 'Trimitere cod MFA eșuată',
  account_mfa_otp_verified: 'Cod MFA verificat',
  account_mfa_otp_verify_fail: 'Verificare cod MFA eșuată',
  account_mfa_backup_code_verified: 'Cod de backup utilizat',
  account_mfa_backup_code_verify_fail: 'Verificare cod de backup eșuată',
  account_mfa_trusted_device_added: 'Dispozitiv de încredere adăugat',
  account_mfa_trusted_device_removed: 'Dispozitiv de încredere eliminat',
  account_mfa_trusted_devices_cleared:
    'Toate dispozitivele de încredere eliminate',
  account_mfa_account_locked: 'Cont blocat temporar',
  account_mfa_enabled: 'MFA activat',
  account_mfa_disabled: 'MFA dezactivat',
  account_mfa_backup_codes_regenerated: 'Coduri de recuperare regenerate',
  account_passkey_registered: 'Passkey înregistrat',
  account_passkey_deleted: 'Passkey eliminat',
  account_passkey_renamed: 'Passkey redenumit',
  account_passkey_auth_success: 'Autentificare cu passkey reușită',
  account_passkey_auth_fail: 'Autentificare cu passkey eșuată',
  workspace_security_auth_method_updated: 'Metodă de autentificare actualizată',
  workspace_created: 'Workspace creat',
  workspace_updated: 'Workspace actualizat',
  workspace_security_updated: 'Securitatea workspace-ului a fost actualizată',
  workspace_security_policy_updated:
    'Politica de securitate a workspace-ului a fost actualizată',
  workspace_member_invited: 'Membru invitat',
  workspace_member_updated: 'Membru actualizat',
  workspace_member_removed: 'Membru eliminat',
  workspace_role_created: 'Rol workspace creat',
  workspace_role_updated: 'Rol workspace actualizat',
  workspace_role_deleted: 'Rol workspace șters',
  workspace_sso_connection_created: 'Conexiune SSO creată',
  workspace_sso_connection_updated: 'Conexiune SSO actualizată',
  workspace_sso_connection_removed: 'Conexiune SSO eliminată',
  workspace_sso_certificate_rotated: 'Certificat SSO rotit',
  workspace_scim_connection_updated: 'Conexiune SCIM actualizată',
  workspace_scim_connection_removed: 'Conexiune SCIM eliminată',
  workspace_scim_sync: 'Sincronizare SCIM',
  workspace_domain_rule_created: 'Regulă de domeniu creată',
  workspace_domain_rule_updated: 'Regulă de domeniu actualizată',
  workspace_domain_rule_deleted: 'Regulă de domeniu ștearsă',
  workspace_api_key_created: 'Cheie API workspace creată',
  workspace_api_key_revoked: 'Cheie API workspace revocată',
  app_created: 'Aplicație creată',
  app_roles_seeded: 'Roluri implicite aplicație provisionate (mock)',
  app_updated: 'Aplicație actualizată',
  app_permission_created: 'Permisiune personalizată aplicație creată',
  app_permission_deleted: 'Permisiune personalizată aplicație ștearsă',
  app_role_created: 'Rol aplicație creat',
  app_role_updated: 'Rol aplicație actualizat',
  app_role_deleted: 'Rol aplicație șters',
  app_credential_created: 'Credențială aplicație creată',
  app_credential_rotated: 'Credențială aplicație rotită',
  app_credential_revoked: 'Credențială aplicație revocată',
  app_webhook_created: 'Webhook creat',
  app_webhook_updated: 'Webhook actualizat',
  app_webhook_deleted: 'Webhook șters',
  app_webhook_delivery_failed: 'Livrare webhook eșuată'
}

const actionsRu: Record<keyof typeof actionsEn, string> = {
  created: 'Создано',
  updated: 'Обновлено',
  deleted: 'Удалено',
  applied: 'Отклик отправлен',
  scheduled: 'Запланировано',
  completed: 'Завершено',
  account_profile_updated: 'Профиль аккаунта обновлён',
  account_connected_provider_added: 'Подключён способ входа',
  account_connected_provider_removed: 'Отключён способ входа',
  account_security_updated: 'Обновлены настройки безопасности аккаунта',
  account_session_revoked: 'Сессия отозвана',
  account_session_revoke_all: 'Отозваны все остальные сессии',
  account_passkey_added: 'Добавлен passkey',
  account_passkey_removed: 'Удалён passkey',
  account_recovery_codes_regenerated: 'Сгенерированы новые коды восстановления',
  account_trusted_device_revoked: 'Отозвано доверие к устройству',
  account_login_credentials: 'Вход с паролем',
  account_login_google: 'Вход через Google',
  account_login_entraid: 'Вход через Entra ID',
  account_login_mfa: 'Вход через MFA',
  account_login_email_otp: 'Вход по коду из email',
  account_login_apple: 'Вход через Apple',
  account_login_passkey: 'Вход с помощью passkey',
  account_login_firebase: 'Вход (Firebase)',
  account_logout: 'Выход из системы',
  account_register: 'Аккаунт зарегистрирован',
  account_register_google: 'Регистрация через Google',
  account_token_refresh: 'Сессия обновлена',
  account_token_sent: 'Токен подтверждения отправлен',
  account_token_confirmed: 'Токен подтверждения подтверждён',
  account_invited: 'Приглашение принято',
  account_password_changed: 'Пароль изменён',
  account_password_mfa_changed: 'Пароль изменён (через MFA)',
  account_password_reset_requested: 'Отправлено письмо для сброса пароля',
  account_password_reset_confirmed: 'Сброс пароля завершён',
  account_mfa_otp_sent: 'Код MFA отправлен',
  account_mfa_otp_sent_fail: 'Ошибка отправки кода MFA',
  account_mfa_otp_verified: 'Код MFA подтверждён',
  account_mfa_otp_verify_fail: 'Ошибка проверки кода MFA',
  account_mfa_backup_code_verified: 'Использован резервный код',
  account_mfa_backup_code_verify_fail: 'Ошибка проверки резервного кода',
  account_mfa_trusted_device_added: 'Добавлено доверенное устройство',
  account_mfa_trusted_device_removed: 'Удалено доверенное устройство',
  account_mfa_trusted_devices_cleared: 'Удалены все доверенные устройства',
  account_mfa_account_locked: 'Аккаунт временно заблокирован',
  account_mfa_enabled: 'MFA включён',
  account_mfa_disabled: 'MFA отключён',
  account_mfa_backup_codes_regenerated: 'Коды восстановления перегенерированы',
  account_passkey_registered: 'Passkey зарегистрирован',
  account_passkey_deleted: 'Passkey удалён',
  account_passkey_renamed: 'Passkey переименован',
  account_passkey_auth_success: 'Успешный вход через passkey',
  account_passkey_auth_fail: 'Неудачный вход через passkey',
  workspace_security_auth_method_updated: 'Метод аутентификации обновлён',
  workspace_created: 'Рабочая область создана',
  workspace_updated: 'Рабочая область обновлена',
  workspace_security_updated: 'Обновлена безопасность workspace',
  workspace_security_policy_updated:
    'Обновлена политика безопасности workspace',
  workspace_member_invited: 'Приглашён участник',
  workspace_member_updated: 'Участник обновлён',
  workspace_member_removed: 'Участник удалён',
  workspace_role_created: 'Создана роль workspace',
  workspace_role_updated: 'Роль workspace обновлена',
  workspace_role_deleted: 'Роль workspace удалена',
  workspace_sso_connection_created: 'Создано подключение SSO',
  workspace_sso_connection_updated: 'Подключение SSO обновлено',
  workspace_sso_connection_removed: 'Подключение SSO удалено',
  workspace_sso_certificate_rotated: 'Сертификат SSO обновлён',
  workspace_scim_connection_updated: 'Подключение SCIM обновлено',
  workspace_scim_connection_removed: 'Подключение SCIM удалено',
  workspace_scim_sync: 'Синхронизация SCIM',
  workspace_domain_rule_created: 'Создано правило домена',
  workspace_domain_rule_updated: 'Правило домена обновлено',
  workspace_domain_rule_deleted: 'Правило домена удалено',
  workspace_api_key_created: 'Создан ключ API workspace',
  workspace_api_key_revoked: 'Ключ API workspace отозван',
  app_created: 'Приложение создано',
  app_roles_seeded: 'Созданы роли приложения по умолчанию (mock)',
  app_updated: 'Приложение обновлено',
  app_permission_created: 'Создано своё право приложения',
  app_permission_deleted: 'Удалено своё право приложения',
  app_role_created: 'Создана роль приложения',
  app_role_updated: 'Роль приложения обновлена',
  app_role_deleted: 'Роль приложения удалена',
  app_credential_created: 'Созданы учётные данные приложения',
  app_credential_rotated: 'Учётные данные приложения сменены',
  app_credential_revoked: 'Учётные данные приложения отозваны',
  app_webhook_created: 'Создан вебхук',
  app_webhook_updated: 'Вебхук обновлён',
  app_webhook_deleted: 'Вебхук удалён',
  app_webhook_delivery_failed: 'Ошибка доставки вебхука'
}

export const auditBlockEn = {
  severity: {
    low: 'Low',
    medium: 'Medium',
    high: 'High'
  },
  scope: {
    account: 'Account',
    workspace: 'Workspace',
    app: 'App'
  },
  // Plural: these label a filter that narrows to a group, not a single actor.
  actorType: {
    user: 'Users',
    agent: 'Agents',
    api_key: 'API keys',
    system: 'System'
  },
  actions: actionsEn
}

export const auditBlockRo = {
  severity: {
    low: 'Scăzută',
    medium: 'Medie',
    high: 'Ridicată'
  },
  scope: {
    account: 'Cont',
    workspace: 'Workspace',
    app: 'Aplicație'
  },
  actorType: {
    user: 'Utilizatori',
    agent: 'Agenți',
    api_key: 'Chei API',
    system: 'Sistem'
  },
  actions: actionsRo
}

export const auditBlockRu = {
  severity: {
    low: 'Низкая',
    medium: 'Средняя',
    high: 'Высокая'
  },
  scope: {
    account: 'Аккаунт',
    workspace: 'Workspace',
    app: 'Приложение'
  },
  actorType: {
    user: 'Пользователи',
    agent: 'Агенты',
    api_key: 'API-ключи',
    system: 'Система'
  },
  actions: actionsRu
}
